import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '../utils/apiClient';
import { useAuth } from './useAuth';

export interface UserProperty {
  id: string;
  title: string;
  titleAr?: string | null;
  status?: string | null;
  price: number;
  bedrooms: number;
  bathrooms: number;
  sqft: number;
  address: string;
  city: string;
  imageUrl?: string | null;
  galleryImages?: string[];
  propertyType?: string;
  listingType?: string;
}

interface UserPropertiesResponse {
  data?: UserProperty[];
  meta?: { totalItems?: number };
}

interface UseUserPropertiesReturn {
  properties: UserProperty[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useUserProperties(): UseUserPropertiesReturn {
  const { session } = useAuth();
  const token = session?.token ?? null;
  const [properties, setProperties] = useState<UserProperty[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProperties = useCallback(async () => {
    if (!token) {
      setProperties([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiRequest<UserPropertiesResponse>(
        '/properties?mine=true&pageSize=100',
        { token },
      );
      const items = res.data ?? [];
      setProperties(items);
      setTotal(res.meta?.totalItems ?? items.length);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load properties';
      setError(message);
      setProperties([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  return { properties, total, loading, error, refetch: fetchProperties };
}
